import { apiFetch, readJson, readJsonArray, readErrorMessage } from './client';
import type {
  CalendarOwnerKind,
  CalendarTarget,
  CalendarTargetSegment,
  CalendarSyncDirection
} from './timeOff';

export interface DirectoryPrincipal {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string | null;
}

export interface GraphCalendarCandidate {
  graphCalendarId: string | null;
  name: string;
  isDefaultCalendar: boolean;
  ownerKind: CalendarOwnerKind;
  ownerId: string;
}

function qs(query: Record<string, string | undefined>): string {
  const parts = Object.entries(query).filter(([, v]) => v !== undefined && v !== '');
  if (parts.length === 0) return '';
  return `?${parts.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v!)}`).join('&')}`;
}

export interface GraphCalendarCatalogRow {
  id: string;
  ownerKind: string;
  graphOwnerId: string;
  graphCalendarKey: string;
  name: string;
  isDefaultCalendar: boolean;
  lastFetchedAt: string;
}

export async function listCalendarCatalog(ownerId: string): Promise<GraphCalendarCatalogRow[]> {
  const res = await apiFetch(`/api/admin/time-off/catalog${qs({ ownerId })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<GraphCalendarCatalogRow>(res);
}

export async function refreshCalendarCatalog(): Promise<void> {
  const res = await apiFetch('/api/admin/time-off/catalog/refresh', { method: 'POST' });
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export async function searchDirectoryGroups(q: string): Promise<DirectoryPrincipal[]> {
  const res = await apiFetch(`/api/admin/time-off/directory/groups${qs({ q })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<DirectoryPrincipal>(res);
}

export async function searchDirectoryUsers(q: string): Promise<DirectoryPrincipal[]> {
  const res = await apiFetch(`/api/admin/time-off/directory/users${qs({ q })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<DirectoryPrincipal>(res);
}

export async function listGraphCalendars(
  ownerKind: CalendarOwnerKind,
  ownerId: string
): Promise<GraphCalendarCandidate[]> {
  const res = await apiFetch(
    `/api/admin/time-off/graph-calendars${qs({
      ownerKind,
      ownerId
    })}`
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<GraphCalendarCandidate>(res);
}

export interface CreateCalendarTargetPayload {
  displayName: string;
  segment: CalendarTargetSegment;
  graphCalendarOwnerKind: CalendarOwnerKind;
  graphOwnerId: string;
  graphCalendarId?: string | null;
  isEnabled?: boolean | null;
  syncDirection?: CalendarSyncDirection | null;
}

export async function createCalendarTarget(body: CreateCalendarTargetPayload): Promise<CalendarTarget> {
  const res = await apiFetch('/api/time-off/calendars', {
    method: 'POST',
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<CalendarTarget>(res);
}

export interface UpdateCalendarTargetPayload {
  displayName?: string | null;
  segment?: CalendarTargetSegment | null;
  graphCalendarOwnerKind?: CalendarOwnerKind | null;
  graphOwnerId?: string | null;
  graphCalendarId?: string | null;
  isEnabled?: boolean | null;
  syncDirection?: CalendarSyncDirection | null;
  clearInboundCalendarEventsDeltaLink?: boolean | null;
}

export async function updateCalendarTarget(id: string, body: UpdateCalendarTargetPayload): Promise<CalendarTarget> {
  const res = await apiFetch(`/api/time-off/calendars/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<CalendarTarget>(res);
}

export async function deleteCalendarTarget(id: string): Promise<void> {
  const res = await apiFetch(`/api/time-off/calendars/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) throw new Error(await readErrorMessage(res));
}
